'use client'

import {
   ActionIcon,
   AppShell,
   Burger,
   Button,
   Container,
   Group,
   LogoHorizontal,
   Stack
} from '@blocks'
import {
   primaryCallToActionLabel,
   siteNavigationBackdropHex
} from '@constants'
import {useDisclosure, useMediaQuery} from '@hooks'
import {IconArrowRight, IconArrowUp} from '@icons'
import {
   SITE_LAYOUT_HEADER_DESKTOP_MEDIA_QUERY,
   SITE_LAYOUT_HEADER_HEIGHT_DESKTOP_PX,
   SITE_LAYOUT_HEADER_HEIGHT_MOBILE_PX
} from '@utilities'
import {usePathname, useRouter} from 'next/navigation'
import {type ReactNode, useEffect, useState} from 'react'
import {Footer} from '@/blocks/Footer'

type Payload = {
   children: ReactNode
}

const FLOATING_SCROLL_TOP_BUTTON_MIN_SCROLL_PX = 360

const navLinks = [
   {label: 'Home', href: '/'},
   {label: 'Car Wash', href: '/car-wash'},
   {label: 'Dog Wash', href: '/dog-wash'},
   {label: 'Fleet', href: '/fleet'},
   {label: 'Detailing & Grooming', href: '/detailing'},
   {label: 'About', href: '/about'}
]

export const Navigation = ({children}: Payload) => {
   const router = useRouter()
   const pathname = usePathname()
   const isDesktop = useMediaQuery(
      SITE_LAYOUT_HEADER_DESKTOP_MEDIA_QUERY
   )
   const [opened, {toggle}] = useDisclosure()
   const [
      showFloatingScrollTopButton,
      setShowFloatingScrollTopButton
   ] = useState(false)

   useEffect(() => {
      const updateFloatingScrollTopVisibility = () => {
         setShowFloatingScrollTopButton(
            window.scrollY > FLOATING_SCROLL_TOP_BUTTON_MIN_SCROLL_PX
         )
      }

      updateFloatingScrollTopVisibility()
      window.addEventListener(
         'scroll',
         updateFloatingScrollTopVisibility,
         {
            passive: true
         }
      )
      window.addEventListener(
         'resize',
         updateFloatingScrollTopVisibility
      )
      window.addEventListener(
         'scrollend',
         updateFloatingScrollTopVisibility
      )

      return () => {
         window.removeEventListener(
            'scroll',
            updateFloatingScrollTopVisibility
         )
         window.removeEventListener(
            'resize',
            updateFloatingScrollTopVisibility
         )
         window.removeEventListener(
            'scrollend',
            updateFloatingScrollTopVisibility
         )
      }
   }, [])

   const isActive = (href: string) =>
      href === '/' ? pathname === '/' : pathname.startsWith(href)

   return (
      <>
         <AppShell
            header={{
               height: isDesktop
                  ? SITE_LAYOUT_HEADER_HEIGHT_DESKTOP_PX
                  : SITE_LAYOUT_HEADER_HEIGHT_MOBILE_PX
            }}
            withBorder={false}
            padding={0}
            navbar={{
               width: 300,
               breakpoint: 'sm',
               collapsed: {desktop: true, mobile: !opened}
            }}
         >
            <AppShell.Header
               bg={siteNavigationBackdropHex}
               style={{
                  backdropFilter: 'blur(8px)'
               }}
            >
               <Container
                  h={'100%'}
                  size={'xl'}
               >
                  <Group
                     h={'100%'}
                     align={'center'}
                  >
                     <Group
                        justify={'space-between'}
                        style={{flex: 1}}
                     >
                        <LogoHorizontal
                           width={isDesktop ? 300 : 170}
                           height={isDesktop ? 80 : 36}
                        />
                        <Group
                           visibleFrom={'sm'}
                           align={'center'}
                           gap={16}
                           wrap={'nowrap'}
                        >
                           {navLinks.map((link) => (
                              <Button
                                 c={
                                    isActive(link.href)
                                       ? 'white'
                                       : '#ffffffc2'
                                 }
                                 px={4}
                                 fz={isActive(link.href) ? 19 : 19}
                                 fw={isActive(link.href) ? 700 : 600}
                                 key={link.href}
                                 radius={'md'}
                                 variant={'transparent'}
                                 onClick={() =>
                                    router.push(link.href)
                                 }
                              >
                                 {link.label}
                              </Button>
                           ))}
                        </Group>
                        <Group
                           visibleFrom={'sm'}
                           align={'center'}
                           gap={20}
                        >
                           <Button
                              c={'#163b6e'}
                              fw={700}
                              size={'lg'}
                              radius={'xl'}
                              color={'primary'}
                              variant={'white'}
                              rightSection={
                                 <IconArrowRight
                                    size={22}
                                    stroke={1.5}
                                 />
                              }
                              onClick={() => router.push('/contact')}
                           >
                              {primaryCallToActionLabel}
                           </Button>
                        </Group>
                     </Group>
                     <Burger
                        aria-expanded={opened}
                        aria-label={
                           opened
                              ? 'Close navigation menu'
                              : 'Open navigation menu'
                        }
                        color={'white'}
                        size={'sm'}
                        hiddenFrom={'sm'}
                        opened={opened}
                        onClick={toggle}
                     />
                  </Group>
               </Container>
            </AppShell.Header>
            <AppShell.Navbar
               py={'md'}
               px={4}
               style={{
                  backgroundColor: siteNavigationBackdropHex,
                  backdropFilter: 'blur(8px)'
               }}
            >
               <Stack
                  h={'100%'}
                  p={10}
                  gap={24}
                  align={'flex-end'}
                  justify={'space-between'}
               >
                  <Stack align={'flex-end'}>
                     {navLinks.map((link) => (
                        <Button
                           key={link.href}
                           c={
                              isActive(link.href)
                                 ? 'white'
                                 : '#ffffffc2'
                           }
                           fz={22}
                           fw={isActive(link.href) ? 700 : 450}
                           variant={'transparent'}
                           onClick={() => {
                              router.push(link.href)
                              toggle()
                           }}
                        >
                           {link.label}
                        </Button>
                     ))}
                  </Stack>
                  <Stack
                     w={opened ? '100%' : 'auto'}
                     p={0}
                     align={'center'}
                     justify={'center'}
                  >
                     <Button
                        c={'#163b6e'}
                        fw={700}
                        fz={19}
                        mb={40}
                        size={'lg'}
                        radius={'xl'}
                        color={'primary'}
                        variant={'white'}
                        rightSection={
                           <IconArrowRight
                              size={22}
                              stroke={1.5}
                           />
                        }
                        onClick={() => {
                           router.push('/contact')
                           toggle()
                        }}
                     >
                        {primaryCallToActionLabel}
                     </Button>
                  </Stack>
               </Stack>
            </AppShell.Navbar>
            <AppShell.Main
               style={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0
               }}
            >
               {children}
               <Footer />
            </AppShell.Main>
         </AppShell>
         <ActionIcon
            aria-label={'Scroll to top'}
            gradient={{
               from: '#7dd3fc',
               to: '#38bdf8',
               deg: 120
            }}
            radius={'xl'}
            size={'xl'}
            style={{
               position: 'fixed',
               right: 'max(1rem, calc(1rem + env(safe-area-inset-right, 0px)))',
               bottom:
                  'max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))',
               zIndex: 9000,
               boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
               opacity: showFloatingScrollTopButton ? 1 : 0,
               transform: showFloatingScrollTopButton
                  ? 'translateY(0) scale(1)'
                  : 'translateY(8px) scale(0.92)',
               pointerEvents: showFloatingScrollTopButton
                  ? 'auto'
                  : 'none',
               transition: 'opacity 0.22s ease, transform 0.22s ease'
            }}
            variant={'gradient'}
            onClick={() =>
               window.scrollTo({top: 0, behavior: 'auto'})
            }
         >
            <IconArrowUp
               color={'#163b6e'}
               size={22}
               stroke={2.5}
            />
         </ActionIcon>
      </>
   )
}
